import './index.css';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';
import minMax from 'dayjs/plugin/minMax';
import isToday from 'dayjs/plugin/isToday';
import timezone from 'dayjs/plugin/timezone';
import duration from 'dayjs/plugin/duration';
import { LicenseInfo } from '@mui/x-license';
import { createRoot } from 'react-dom/client';
import isBetween from 'dayjs/plugin/isBetween';
import localeData from 'dayjs/plugin/localeData';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import isYesterday from 'dayjs/plugin/isYesterday';
import relativeTime from 'dayjs/plugin/relativeTime';
import { NotificationsProvider } from '@toolpad/core';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { LocalizationProvider } from '@mui/x-date-pickers';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { RouterProvider, createBrowserRouter } from 'react-router';

import { App } from 'app';
import { store } from 'store';
import { Config } from 'config';
import { interceptor } from 'api/instance';
import { LoaderRedux, ErrorBoundary } from 'components';

import reportWebVitals from './reportWebVitals';

const { sentry, routeBasename, MUILicenseKey } = Config;
const isProductionTelemetry = sentry.env === 'production';

LicenseInfo.setLicenseKey(MUILicenseKey);

dayjs.extend(utc);
dayjs.extend(isToday);
dayjs.extend(minMax);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(localeData);
dayjs.extend(isTomorrow);
dayjs.extend(isYesterday);
dayjs.extend(relativeTime);
dayjs.extend(advancedFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(localizedFormat);
dayjs.extend(customParseFormat);

if (sentry.dsn) {
  if (sentry.release) {
    console.log(`VERSION ${sentry.release}`);
  }
  try {
    Sentry.init({
      dsn: sentry.dsn,
      environment: sentry.env,
      release: sentry.release,
      tracesSampleRate: isProductionTelemetry ? 0 : 1.0,
      replaysSessionSampleRate: isProductionTelemetry ? 0 : 0.1,
      replaysOnErrorSampleRate: isProductionTelemetry ? 0 : 1.0,
      ignoreErrors: [
        'AxiosError',
        'TimeoutError',
        'Loading chunk',
        "Unexpected token '<'",
        'Failed to fetch dynamically',
      ],
      integrations: isProductionTelemetry
        ? [Sentry.consoleLoggingIntegration()]
        : [
            Sentry.replayIntegration(),
            Sentry.browserTracingIntegration(),
            Sentry.consoleLoggingIntegration(),
          ],
    });
    Sentry.setUser(null);
  } catch (error) {
    console.error(error);
  }
}

interceptor(store.dispatch);

if (Config.waf.apiUrl) {
  const AwsWafScript = document.createElement('script');
  AwsWafScript.id = 'AwsWAFScript';
  AwsWafScript.async = false;
  AwsWafScript.src = Config.waf.apiUrl;
  document.head.appendChild(AwsWafScript);
}

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  <Provider store={store}>
    <RouterProvider
      router={createBrowserRouter(
        [
          {
            path: '*',
            element: (
              <NotificationsProvider>
                <ErrorBoundary>
                  <LoaderRedux>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <App />
                    </LocalizationProvider>
                  </LoaderRedux>
                </ErrorBoundary>
              </NotificationsProvider>
            ),
          },
        ],
        {
          basename: routeBasename,
        }
      )}
    />
  </Provider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
